import { ShoppingBagIcon } from '@heroicons/react/outline'
import { CollectionIcon, PlusIcon } from '@heroicons/react/solid'
import { ChevronRightIcon as ChevronRightIconOutline } from '@heroicons/react/outline'
import { useGetCustomer, useGetCustomerStats } from 'api/Customers'
import { useAddOrder } from 'api/Orders'
import { useParams, useHistory } from 'react-router-dom'
import { Button } from 'ui/components/Button'
import { EmptyState } from 'ui/components/EmptyState'
import { Section } from '../Shared/Section'
import { DateTime } from 'luxon'
import { LinkWithPreload } from 'utilities/PreloadLinks'
import { StatusTag } from 'screens/OrderDetails/components/Overview/Overview'
import pluralize from 'pluralize'
import { useAnalytics } from 'utilities/analytics'
import { useCurrentUserCanForCurrentBrand } from 'hooks/UseCurrentUserCan'
import { RESERVATIONS_MANAGE, RESERVATIONS_VIEW } from 'constants/permissions'
import { useTranslation } from 'react-i18next'

const CreateOrderButton = () => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanManageReservations = can(RESERVATIONS_MANAGE)
  const { clientId } = useParams<{ clientId }>()
  const history = useHistory()
  const { isLoading, mutateAsync } = useAddOrder()
  const { fireEvent } = useAnalytics()

  const handleNewOrder = async () => {
    fireEvent('order_created', { via: 'client_details' })

    const { data: order } = await mutateAsync({
      customer_id: clientId,
    } as any)

    history.push(`/orders/${order.id}`)
  }

  return (
    <Button
      label="New"
      loading={isLoading}
      leadingIcon={PlusIcon}
      onClick={handleNewOrder}
      disabled={!userCanManageReservations}
      disabledReason={t('tooltip.missing_permission.reservations_manage')}
    />
  )
}

const Order = ({ order }) => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanViewReservations = can(RESERVATIONS_VIEW)

  return (
    <LinkWithPreload
      className={`flex justify-between items-center p-3 hover:bg-violet-50 w-full ${
        !userCanViewReservations && 'cursor-not-allowed'
      }`}
      to={`/orders/${order.id}`}
      disabled={!userCanViewReservations}
      disabledReason={t('tooltip.missing_permission.reservations_view')}
    >
      <div>
        <div className="flex space-x-1 items-center">
          <div className="font-medium">
            {order.order_ref ?? <span className="italic">No ref</span>}
          </div>
          <StatusTag status={order.status} />
        </div>
        <div className="text-gray-500 truncate">
          {order.site && (
            <>
              <span>{order.site.name}</span>
              <span> &middot; </span>
            </>
          )}
          <span>
            Received {DateTime.fromJSDate(order.submitted_at).toLocaleString(DateTime.DATETIME_MED)}
          </span>
          <span> &middot; </span>
          <span>
            {order.num_items} {pluralize('item', order.num_items)}
          </span>
        </div>
      </div>
      <div>
        <ChevronRightIconOutline className="w-6 h-6 text-gray-400" />
      </div>
    </LinkWithPreload>
  )
}

export const Orders = () => {
  const { t } = useTranslation()
  const can = useCurrentUserCanForCurrentBrand()
  const userCanViewReservations = can(RESERVATIONS_VIEW)
  const { clientId } = useParams<{ clientId }>()
  const history = useHistory()

  const { isLoading: isLoadingClient, data: { data: client = {} } = {} } = useGetCustomer(clientId)

  const {
    isLoading: isLoadingStats,
    data: { data: { recent_orders: statsOrders = [] } = {} } = {},
  } = useGetCustomerStats(clientId)

  const displayOrders = statsOrders.slice(0, 5)
  const isLoading = isLoadingStats

  return (
    <Section
      title="Orders"
      button={
        <div className="flex gap-2">
          {!isLoadingClient && (
            <Button
              variant="ghost"
              leadingIcon={CollectionIcon}
              label={t('clients.client_profile.orders.view_all_button')}
              onClick={() => history.push(`/orders?customer_id=${clientId}`)}
              disabled={!userCanViewReservations}
              disabledReason={t('tooltip.missing_permission.reservations_view')}
            />
          )}
          {!isLoadingClient && client.deleted_at === null && <CreateOrderButton />}
        </div>
      }
    >
      {isLoading && <div className="bg-grape-100 animate-pulse h-16" />}

      {!isLoading && displayOrders.length === 0 && (
        <EmptyState title="This client has no orders" icon={ShoppingBagIcon} />
      )}

      {!isLoading && displayOrders.length > 0 && (
        <ul className="text-sm divide-y divide-violet-100">
          {displayOrders.map((order) => (
            <Order key={order.id} order={order} />
          ))}
        </ul>
      )}
    </Section>
  )
}
